import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Check, Heart, PartyPopper, Building2, Users, Sparkles, ShieldCheck, Wine, Beer, GlassWater, Percent } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

// Drink menu data
const DRINK_MENU = {
  draughts: ["Camden Hells", "Asahi", "Brewdog Lost Lager", "Madri", "Guinness", "Neck Oil", "Camden Pale", "Brewdog Punk IPA", "Inches", "Brewdog Wingman", "Stowford Press"],
  bottledCanned: ["Peroni", "Punk IPA", "Heineken 0%", "Doom Bar", "Fruity Ciders", "Craft Beers"],
  wines: ["Red (Merlot)", "White (Sauvignon/Pinot)", "Rosé", "Prosecco", "Champagne"],
  spirits: ["Vodka", "Gin", "Craft Gins", "Whisky", "Rum (White)", "Rum (Spiced)", "Tequila", "Brandy/Cognac"],
  softs: ["Cola", "Lemonade", "Orange Juice", "Apple Juice", "Tonic Water", "Sparkling Water"],
  shots: ["Tequila", "Tequila Rose", "Baby Guinness", "Sambuca"]
};

const EventTypeButton = ({ value, label, icon, isSelected, onClick }) => (
  <button
    onClick={() => onClick(value)}
    data-testid={`event-type-${value}`}
    aria-pressed={isSelected}
    aria-label={`Select ${label} event type`}
    className={`p-4 md:p-6 rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${
      isSelected ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"
    }`}
  >
    <div className={`mb-2 md:mb-3 ${isSelected ? "text-[#a5dae0]" : "text-[#555555]"}`}>{icon}</div>
    <p className="font-semibold text-[#1a1a1a] text-sm md:text-base">{label}</p>
  </button>
);

const VenueButton = ({ type, isSelected, onClick }) => (
  <button
    onClick={() => onClick(type)}
    data-testid={`venue-${type}`}
    aria-pressed={isSelected}
    aria-label={`Select ${type} venue`}
    className={`p-3 md:p-4 rounded-xl border-2 capitalize transition-all text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${
      isSelected ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"
    }`}
  >
    {type}
  </button>
);

const BarOption = ({ value, label, price, desc, isSelected, onClick, isMultiSelect }) => (
  <button
    onClick={() => onClick(value)}
    data-testid={`bar-${value}`}
    aria-pressed={isSelected}
    aria-label={`${isSelected ? 'Deselect' : 'Select'} ${label} - ${price}`}
    className={`w-full p-4 md:p-6 rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${
      isSelected ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"
    }`}
  >
    <div className="flex justify-between items-start gap-2">
      <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
        {isMultiSelect && (
          <div 
            className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
              isSelected ? "bg-[#a5dae0] border-[#a5dae0]" : "border-gray-400"
            }`}
            aria-hidden="true"
          >
            {isSelected && <Check size={14} className="text-white" />}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-[#1a1a1a] text-base md:text-lg">{label}</p>
          <p className="text-[#555555] text-xs md:text-sm mt-1">{desc}</p>
        </div>
      </div>
      <span className="text-[#a5dae0] font-bold text-sm md:text-base flex-shrink-0">{price}</span>
    </div>
  </button>
);

const PackageOption = ({ value, label, desc, isSelected, onClick }) => (
  <button
    onClick={() => onClick(value)}
    data-testid={`package-${value}`}
    aria-pressed={isSelected}
    aria-label={`Select ${label} package`}
    className={`w-full p-4 md:p-6 rounded-xl border-2 text-left transition-all focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${
      isSelected ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"
    }`}
  >
    <p className="font-semibold text-[#1a1a1a] text-base md:text-lg">{label}</p>
    <p className="text-[#555555] text-xs md:text-sm mt-1">{desc}</p>
  </button>
);

const DrinkCategorySelect = ({ category, label, icon, items, selectedItems, onToggle }) => (
  <div className="bg-gray-50 rounded-xl p-3 md:p-4" role="group" aria-labelledby={`drink-category-${category}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[#a5dae0]" aria-hidden="true">{icon}</span>
      <h4 id={`drink-category-${category}`} className="font-semibold text-[#444444] text-sm md:text-base">{label}</h4>
    </div>
    <div className="flex flex-wrap gap-1.5 md:gap-2">
      {items.map((item) => (
        <button
          key={item}
          onClick={() => onToggle(category, item)}
          aria-pressed={selectedItems.includes(item)}
          aria-label={`${selectedItems.includes(item) ? 'Remove' : 'Add'} ${item}`}
          className={`px-2.5 py-1 md:px-3 md:py-1.5 rounded-full text-xs md:text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-1 ${
            selectedItems.includes(item)
              ? "bg-[#a5dae0] text-[#1a1a1a]"
              : "bg-white border border-gray-200 text-[#666666] hover:border-[#a5dae0]"
          }`}
        >
          {item}
        </button>
      ))}
    </div>
  </div>
);

const StepIndicator = ({ stepNum, currentStep, title }) => (
  <div 
    className={`flex items-center gap-1 md:gap-2 ${currentStep >= stepNum ? "text-[#a5dae0]" : "text-[#555555]"}`}
    aria-current={currentStep === stepNum ? "step" : undefined}
  >
    <div
      className={`w-7 h-7 md:w-8 lg:w-10 md:h-8 lg:h-10 rounded-full flex items-center justify-center font-semibold text-xs md:text-sm transition-all ${
        currentStep >= stepNum ? "bg-[#a5dae0] text-[#1a1a1a]" : "bg-[#e5e5e5] text-[#555555]"
      }`}
      aria-label={`Step ${stepNum}: ${title}${currentStep > stepNum ? ' (completed)' : currentStep === stepNum ? ' (current)' : ''}`}
    >
      {currentStep > stepNum ? <Check size={14} aria-hidden="true" /> : stepNum}
    </div>
    <span className="hidden lg:block text-xs font-medium">{title}</span>
  </div>
);

const BookingForm = ({ isModal = false, onClose }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    event_type: "",
    event_date: "",
    event_location: "",
    venue_type: "",
    bar_types: [], // Changed to array for multiple selection
    guest_count: 50,
    event_duration: 5,
    package_type: "",
    token_count: 50, // New: for token bar
    glassware: "disposable",
    day_package: false,
    drink_preferences: {
      draughts: [],
      bottledCanned: [],
      wines: [],
      spirits: [],
      softs: [],
      shots: []
    },
    customer_name: "",
    email: "",
    phone: "",
    callback_date: "",
    callback_time: "",
    additional_notes: ""
  });

  // Track abandoned form - capture lead when user has contact info but leaves
  const captureAbandonedLead = useCallback(async () => {
    // Only capture if we have contact details and form wasn't submitted
    const hasContactInfo = formData.customer_name || formData.email || formData.phone;
    
    if (hasContactInfo && !formSubmitted && !leadCaptured) {
      try {
        await axios.post(`${API}/abandoned-lead`, {
          customer_name: formData.customer_name,
          email: formData.email,
          phone: formData.phone,
          event_type: formData.event_type,
          event_date: formData.event_date,
          event_location: formData.event_location,
          guest_count: formData.guest_count,
          bar_types: formData.bar_types,
          package_type: formData.package_type,
          current_step: step
        });
        setLeadCaptured(true);
      } catch (error) {
        console.error("Failed to capture abandoned lead:", error);
      }
    }
  }, [formData, formSubmitted, leadCaptured, step]);

  // Capture lead when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      captureAbandonedLead();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        captureAbandonedLead();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [captureAbandonedLead]);

  // Also capture lead after 5 minutes of inactivity on step 5 (contact details page)
  useEffect(() => {
    let inactivityTimer;
    const hasContactInfo = formData.customer_name || formData.email || formData.phone;
    
    if (step === 5 && hasContactInfo && !formSubmitted && !leadCaptured) {
      inactivityTimer = setTimeout(() => {
        captureAbandonedLead();
      }, 300000); // 5 minutes (300,000 milliseconds)
    }

    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [step, formData.customer_name, formData.email, formData.phone, formSubmitted, leadCaptured, captureAbandonedLead]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleBarType = (barType) => {
    setFormData(prev => {
      const currentTypes = prev.bar_types;
      if (currentTypes.includes(barType)) {
        return { ...prev, bar_types: currentTypes.filter(t => t !== barType) };
      } else {
        return { ...prev, bar_types: [...currentTypes, barType] };
      }
    });
  };

  const toggleDrinkPreference = (category, item) => {
    setFormData(prev => {
      const currentItems = prev.drink_preferences[category];
      const newItems = currentItems.includes(item)
        ? currentItems.filter(i => i !== item)
        : [...currentItems, item];
      return {
        ...prev,
        drink_preferences: {
          ...prev.drink_preferences,
          [category]: newItems
        }
      };
    });
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token || "");
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken("");
  };

  // Calculate token price based on guest count
  const getTokenPrice = () => {
    return formData.guest_count > 250 ? 4 : 5;
  };

  // Check if all-inclusive discount applies (duration > 6 hours)
  const hasAllInclusiveDiscount = () => {
    return formData.package_type === "all_inclusive" && formData.event_duration > 6;
  };

  const calculatePrice = useCallback(async () => {
    if (formData.bar_types.length > 0 && formData.package_type) {
      try {
        const response = await axios.post(`${API}/calculate-price`, {
          bar_types: formData.bar_types,
          guest_count: formData.guest_count,
          event_duration: formData.event_duration,
          package_type: formData.package_type,
          token_count: formData.token_count,
          glassware: formData.glassware,
          day_package: formData.day_package
        });
        setPriceData(response.data);
      } catch (error) {
        console.error("Price calculation error:", error);
      }
    }
  }, [formData.bar_types, formData.package_type, formData.guest_count, formData.event_duration, formData.token_count, formData.glassware, formData.day_package]);

  useEffect(() => {
    if (step === 6) {
      calculatePrice();
    }
  }, [step, calculatePrice]);

  const handleSubmit = async () => {
    if (!recaptchaToken) {
      toast.error("Please complete the CAPTCHA verification.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/booking-inquiry`, {
        ...formData,
        bar_type: formData.bar_types.join(", "), // Convert array to string for backend compatibility
        recaptcha_token: recaptchaToken
      });
      setFormSubmitted(true); // Mark as submitted to prevent abandoned lead capture
      toast.success("Booking inquiry submitted! Check your email for confirmation.");
      setStep(1);
      setFormData({
        event_type: "", event_date: "", event_location: "", venue_type: "",
        bar_types: [], guest_count: 50, event_duration: 5, package_type: "",
        token_count: 50, glassware: "disposable", day_package: false,
        drink_preferences: { draughts: [], bottledCanned: [], wines: [], spirits: [], softs: [], shots: [] },
        customer_name: "", email: "", phone: "", callback_date: "", callback_time: "", additional_notes: ""
      });
      setPriceData(null);
      setRecaptchaToken("");
      setFormSubmitted(false); // Reset for next form fill
      setLeadCaptured(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("CAPTCHA verification failed. Please try again.");
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken("");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    }
    setIsSubmitting(false);
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.event_type;
      case 2: return formData.event_date && formData.event_location && formData.venue_type;
      case 3: return formData.bar_types.length > 0;
      case 4: return formData.package_type;
      case 5: return formData.customer_name && formData.email && formData.phone; // Contact details now step 5
      case 6: return recaptchaToken; // Drinks + Quote + Submit is now step 6
      default: return false;
    }
  };

  const renderPriceBreakdown = () => {
    if (!priceData || !priceData.breakdown) return null;
    const breakdownItems = Object.entries(priceData.breakdown);
    return (
      <div className="space-y-2">
        {breakdownItems.map(([key, item]) => (
          <div key={key} className="flex justify-between text-sm">
            <div className="flex-1">
              <span className="text-[#666666]">{item.description}</span>
              {item.discount && (
                <span className="ml-2 inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                  <Percent size={12} />
                  {item.discount}% OFF
                </span>
              )}
              {item.note && (
                <span className="block text-xs text-[#888888]">{item.note}</span>
              )}
            </div>
            <div className="text-right">
              {item.original_cost && item.original_cost !== item.cost && (
                <span className="text-[#999999] line-through text-xs mr-2">£{item.original_cost.toFixed(2)}</span>
              )}
              <span className="font-medium">£{item.cost.toFixed(2)}</span>
            </div>
          </div>
        ))}
        {priceData.total_savings > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
            <div className="flex items-center gap-2 text-green-700">
              <Percent size={16} />
              <span className="font-medium">You're saving £{priceData.total_savings.toFixed(2)}!</span>
            </div>
          </div>
        )}
        <div className="border-t border-[#a5dae0]/30 pt-2 mt-2 flex justify-between">
          <span className="font-semibold text-lg">Total Estimate</span>
          <span className="font-bold text-xl text-[#a5dae0]">£{priceData.estimated_total.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const getTotalSteps = () => 6;

  const motionViewProps = isModal
    ? { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }
    : { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  return (
    <section id="booking" className={isModal ? "py-6 md:py-8 bg-[#fafafa]" : "py-12 md:py-20 lg:py-32 bg-[#fafafa]"} aria-labelledby="booking-heading">
      <div className={isModal ? "max-w-4xl mx-auto px-4 md:px-6" : "max-w-4xl mx-auto px-4 md:px-6 lg:px-12"}>
        <motion.div
          {...motionViewProps}
          className="text-center mb-8 md:mb-12"
        >
          <p className="text-[#a5dae0] font-script text-xl md:text-2xl mb-2 md:mb-4">Get Started</p>
          <h2 id="booking-heading" className="text-3xl md:text-4xl lg:text-5xl mb-2 md:mb-4">Get Your Quote</h2>
          <p className="text-[#555555] text-sm md:text-base">Complete the form below for an instant estimate</p>
        </motion.div>

        {/* Step Progress Indicator */}
        <nav aria-label="Form progress" className="mb-8 md:mb-12">
          <ol className="flex items-center justify-center overflow-x-auto pb-2 gap-0">
            <li><StepIndicator stepNum={1} currentStep={step} title="Event" /></li>
            <li className={`w-3 md:w-6 lg:w-8 h-0.5 mx-0.5 md:mx-1 lg:mx-2 ${step > 1 ? "bg-[#a5dae0]" : "bg-[#e5e5e5]"}`} aria-hidden="true" />
            <li><StepIndicator stepNum={2} currentStep={step} title="Details" /></li>
            <li className={`w-3 md:w-6 lg:w-8 h-0.5 mx-0.5 md:mx-1 lg:mx-2 ${step > 2 ? "bg-[#a5dae0]" : "bg-[#e5e5e5]"}`} aria-hidden="true" />
            <li><StepIndicator stepNum={3} currentStep={step} title="Bar" /></li>
            <li className={`w-3 md:w-6 lg:w-8 h-0.5 mx-0.5 md:mx-1 lg:mx-2 ${step > 3 ? "bg-[#a5dae0]" : "bg-[#e5e5e5]"}`} aria-hidden="true" />
            <li><StepIndicator stepNum={4} currentStep={step} title="Package" /></li>
            <li className={`w-3 md:w-6 lg:w-8 h-0.5 mx-0.5 md:mx-1 lg:mx-2 ${step > 4 ? "bg-[#a5dae0]" : "bg-[#e5e5e5]"}`} aria-hidden="true" />
            <li><StepIndicator stepNum={5} currentStep={step} title="Contact" /></li>
            <li className={`w-3 md:w-6 lg:w-8 h-0.5 mx-0.5 md:mx-1 lg:mx-2 ${step > 5 ? "bg-[#a5dae0]" : "bg-[#e5e5e5]"}`} aria-hidden="true" />
            <li><StepIndicator stepNum={6} currentStep={step} title="Quote" /></li>
          </ol>
        </nav>

        <motion.div
          {...motionViewProps}
          className={isModal ? "bg-white rounded-2xl p-4 md:p-8" : "bg-white rounded-2xl shadow-xl p-4 md:p-8 lg:p-12"}
        >
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step1-heading">
                <h3 id="step1-heading" className="text-xl md:text-2xl mb-4 md:mb-6">What type of event are you planning?</h3>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <EventTypeButton value="wedding" label="Wedding" icon={<Heart size={20} aria-hidden="true" />} isSelected={formData.event_type === "wedding"} onClick={(v) => updateField("event_type", v)} />
                  <EventTypeButton value="private" label="Private Party" icon={<PartyPopper size={20} aria-hidden="true" />} isSelected={formData.event_type === "private"} onClick={(v) => updateField("event_type", v)} />
                  <EventTypeButton value="corporate" label="Corporate" icon={<Building2 size={20} aria-hidden="true" />} isSelected={formData.event_type === "corporate"} onClick={(v) => updateField("event_type", v)} />
                  <EventTypeButton value="festival" label="Festival" icon={<Users size={20} aria-hidden="true" />} isSelected={formData.event_type === "festival"} onClick={(v) => updateField("event_type", v)} />
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step2-heading">
                <h3 id="step2-heading" className="text-xl md:text-2xl mb-4 md:mb-6">Tell us about your event</h3>
                <div className="space-y-4 md:space-y-6">
                  <div>
                    <label htmlFor="event-date" className="block text-sm font-medium text-[#444444] mb-2">Event Date <span className="text-red-500" aria-label="required">*</span></label>
                    <input id="event-date" type="date" value={formData.event_date} onChange={(e) => updateField("event_date", e.target.value)} data-testid="event-date" required
                      className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors" />
                  </div>
                  <div>
                    <label htmlFor="event-location" className="block text-sm font-medium text-[#444444] mb-2">Event Location / Venue <span className="text-red-500" aria-label="required">*</span></label>
                    <input id="event-location" type="text" value={formData.event_location} onChange={(e) => updateField("event_location", e.target.value)}
                      placeholder="e.g., Birmingham, London..." data-testid="event-location" required
                      className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#444444] mb-2">Venue Type <span className="text-red-500" aria-label="required">*</span></label>
                    <div className="grid grid-cols-3 gap-2 md:gap-4">
                      <VenueButton type="indoor" isSelected={formData.venue_type === "indoor"} onClick={(v) => updateField("venue_type", v)} />
                      <VenueButton type="outdoor" isSelected={formData.venue_type === "outdoor"} onClick={(v) => updateField("venue_type", v)} />
                      <VenueButton type="marquee" isSelected={formData.venue_type === "marquee"} onClick={(v) => updateField("venue_type", v)} />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step3-heading">
                <h3 id="step3-heading" className="text-xl md:text-2xl mb-1 md:mb-2">Choose your bar</h3>
                <p className="text-[#555555] text-xs md:text-sm mb-4 md:mb-6">Select one or more bar setups for your event</p>
                <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <BarOption value="caravan" label="Caravan Bar" price="£350" desc="Our iconic mobile caravan bar - perfect for outdoor events" isSelected={formData.bar_types.includes("caravan")} onClick={toggleBarType} isMultiSelect={true} />
                  <BarOption value="buildup" label="Build-Up Bar" price="From £250" desc="Flexible setup for indoor venues and marquees" isSelected={formData.bar_types.includes("buildup")} onClick={toggleBarType} isMultiSelect={true} />
                  <BarOption value="marquee_bar" label="Marquee Bar (6m+)" price="£400" desc="Large-scale bar setup for bigger events" isSelected={formData.bar_types.includes("marquee_bar")} onClick={toggleBarType} isMultiSelect={true} />
                </div>
                {formData.bar_types.length > 1 && (
                  <div className="bg-[#a5dae0]/10 rounded-xl p-3 md:p-4 mb-4 md:mb-6" role="status" aria-live="polite">
                    <p className="text-xs md:text-sm text-[#444444]">
                      <span className="font-semibold">Selected: </span>
                      {formData.bar_types.map(t => t === "caravan" ? "Caravan Bar" : t === "buildup" ? "Build-Up Bar" : "Marquee Bar").join(" + ")}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="guest-count" className="block text-sm font-medium text-[#444444] mb-2">Number of Guests: <span className="text-[#a5dae0] font-bold">{formData.guest_count}</span></label>
                    <input id="guest-count" type="range" min="20" max="500" step="10" value={formData.guest_count} onChange={(e) => updateField("guest_count", parseInt(e.target.value))} data-testid="guest-count" aria-valuenow={formData.guest_count} aria-valuemin="20" aria-valuemax="500" className="w-full accent-[#a5dae0] h-2" />
                    <div className="flex justify-between text-xs text-[#555555] mt-1"><span>20</span><span>500</span></div>
                  </div>
                  <div>
                    <label htmlFor="event-duration" className="block text-sm font-medium text-[#444444] mb-2">Duration: <span className="text-[#a5dae0] font-bold">{formData.event_duration} hours</span></label>
                    <input id="event-duration" type="range" min="4" max="12" value={formData.event_duration} onChange={(e) => updateField("event_duration", parseInt(e.target.value))} data-testid="event-duration" aria-valuenow={formData.event_duration} aria-valuemin="4" aria-valuemax="12" className="w-full accent-[#a5dae0] h-2" />
                    <div className="flex justify-between text-xs text-[#555555] mt-1"><span>4h</span><span>12h</span></div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step4-heading">
                <h3 id="step4-heading" className="text-xl md:text-2xl mb-4 md:mb-6">Select your package</h3>
                <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  <PackageOption value="cash_spot" label="Cash Bar" desc="Guests pay for their own drinks. Min spend £800." isSelected={formData.package_type === "cash_spot"} onClick={(v) => updateField("package_type", v)} />
                  <PackageOption value="tab_spot" label="Tab Bar" desc="Flexible budget - you control the spend, guests drink free." isSelected={formData.package_type === "tab_spot"} onClick={(v) => updateField("package_type", v)} />
                  <PackageOption value="token_bar" label="Token Bar" desc={`Pre-purchase drink tokens at £${getTokenPrice()} each for guests.${formData.guest_count > 250 ? " (Bulk discount applied!)" : ""}`} isSelected={formData.package_type === "token_bar"} onClick={(v) => updateField("package_type", v)} />
                  <div className="relative">
                    <PackageOption value="all_inclusive" label="All-Inclusive" desc={`Unlimited drinks for the event. From £5/person/hour.${formData.event_duration > 6 ? " (15% discount for 6+ hours!)" : ""}`} isSelected={formData.package_type === "all_inclusive"} onClick={(v) => updateField("package_type", v)} />
                    {formData.package_type === "all_inclusive" && formData.event_duration > 6 && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1" role="status" aria-label="15 percent discount applied">
                        <Percent size={12} aria-hidden="true" /> 15% OFF
                      </div>
                    )}
                  </div>
                </div>

                {/* Token Count Selector - Only show when Token Bar is selected */}
                {formData.package_type === "token_bar" && (
                  <div className="bg-[#a5dae0]/10 rounded-xl p-4 md:p-6 mb-4 md:mb-6">
                    <h4 className="font-semibold text-[#444444] mb-3 md:mb-4 text-sm md:text-base">How many tokens would you like?</h4>
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                      <input
                        type="number"
                        min="10"
                        max="1000"
                        step="10"
                        value={formData.token_count}
                        onChange={(e) => updateField("token_count", Math.max(10, parseInt(e.target.value) || 10))}
                        data-testid="token-count"
                        aria-label="Number of tokens"
                        className="w-full md:w-32 p-3 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors text-center font-semibold"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-[#444444]">
                          <span className="font-semibold">{formData.token_count} tokens</span> × £{getTokenPrice()} = 
                          <span className="text-[#a5dae0] font-bold ml-1">£{(formData.token_count * getTokenPrice()).toFixed(2)}</span>
                        </p>
                        {formData.guest_count > 250 && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1" role="status">
                            <Percent size={12} aria-hidden="true" />
                            Bulk discount: £4 per token (250+ guests)
                          </p>
                        )}
                      </div>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="500"
                      step="10"
                      value={formData.token_count}
                      onChange={(e) => updateField("token_count", parseInt(e.target.value))}
                      aria-label="Token count slider"
                      aria-valuenow={formData.token_count}
                      className="w-full mt-4 accent-[#a5dae0] h-2"
                    />
                    <div className="flex justify-between text-xs text-[#555555] mt-1"><span>10</span><span>500</span></div>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#444444] mb-2">Glassware</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      <button onClick={() => updateField("glassware", "disposable")} data-testid="glassware-disposable" aria-pressed={formData.glassware === "disposable"}
                        className={`p-3 md:p-4 rounded-xl border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${formData.glassware === "disposable" ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"}`}>
                        <p className="font-medium text-sm md:text-base">Disposable Cups</p><p className="text-xs md:text-sm text-[#555555]">Included</p>
                      </button>
                      <button onClick={() => updateField("glassware", "reusable")} data-testid="glassware-reusable" aria-pressed={formData.glassware === "reusable"}
                        className={`p-3 md:p-4 rounded-xl border-2 transition-all text-left focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${formData.glassware === "reusable" ? "border-[#a5dae0] bg-[#a5dae0]/10" : "border-gray-200 hover:border-[#a5dae0]/50"}`}>
                        <p className="font-medium text-sm md:text-base">Reusable Glassware</p><p className="text-xs md:text-sm text-[#555555]">£2/person + £100 deposit</p>
                      </button>
                    </div>
                  </div>
                  {formData.event_type === "wedding" && (
                    <div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={formData.day_package} onChange={(e) => updateField("day_package", e.target.checked)} data-testid="day-package" aria-describedby="day-package-desc" className="w-5 h-5 accent-[#a5dae0] mt-0.5 flex-shrink-0" />
                        <span><span className="font-medium text-[#1a1a1a] text-sm md:text-base">Add Wedding Day Package</span><span id="day-package-desc" className="text-[#555555] text-xs md:text-sm block">£15.50/person - Welcome drink, table wine, toasting drinks</span></span>
                      </label>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="step5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step5-heading">
                <h3 id="step5-heading" className="text-xl md:text-2xl mb-4 md:mb-6">Your contact details</h3>
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label htmlFor="customer-name" className="block text-sm font-medium text-[#444444] mb-2">Your Name <span className="text-red-500" aria-label="required">*</span></label>
                    <input id="customer-name" type="text" value={formData.customer_name} onChange={(e) => updateField("customer_name", e.target.value)} placeholder="John Smith" data-testid="customer-name" required autoComplete="name"
                      className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label htmlFor="customer-email" className="block text-sm font-medium text-[#444444] mb-2">Email <span className="text-red-500" aria-label="required">*</span></label>
                      <input id="customer-email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} placeholder="john@example.com" data-testid="customer-email" required autoComplete="email"
                        className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors" />
                    </div>
                    <div>
                      <label htmlFor="customer-phone" className="block text-sm font-medium text-[#444444] mb-2">Phone <span className="text-red-500" aria-label="required">*</span></label>
                      <input id="customer-phone" type="tel" value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="07XXX XXXXXX" data-testid="customer-phone" required autoComplete="tel"
                        className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors" />
                    </div>
                  </div>
                  <fieldset className="bg-[#fafafa] rounded-xl p-3 md:p-4 border border-gray-200">
                    <legend className="text-sm font-medium text-[#444444] mb-2 md:mb-3">Preferred callback time (optional)</legend>
                    <p className="text-xs text-[#555555] mb-2 md:mb-3">Let us know when's best to call you back to discuss your booking.</p>
                    <div className="grid grid-cols-2 gap-2 md:gap-4">
                      <div>
                        <label htmlFor="callback-date" className="block text-xs text-[#555555] mb-1">Date</label>
                        <input id="callback-date" type="date" value={formData.callback_date} onChange={(e) => updateField("callback_date", e.target.value)} data-testid="callback-date"
                          className="w-full p-2 md:p-3 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors text-sm" />
                      </div>
                      <div>
                        <label htmlFor="callback-time" className="block text-xs text-[#555555] mb-1">Time</label>
                        <select id="callback-time" value={formData.callback_time} onChange={(e) => updateField("callback_time", e.target.value)} data-testid="callback-time"
                          className="w-full p-2 md:p-3 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors text-sm bg-white">
                          <option value="">Any time</option>
                          <option value="09:00 - 10:00">09:00 - 10:00</option>
                          <option value="10:00 - 11:00">10:00 - 11:00</option>
                          <option value="11:00 - 12:00">11:00 - 12:00</option>
                          <option value="12:00 - 13:00">12:00 - 13:00</option>
                          <option value="13:00 - 14:00">13:00 - 14:00</option>
                          <option value="14:00 - 15:00">14:00 - 15:00</option>
                          <option value="15:00 - 16:00">15:00 - 16:00</option>
                          <option value="16:00 - 17:00">16:00 - 17:00</option>
                          <option value="17:00 - 18:00">17:00 - 18:00</option>
                          <option value="18:00 - 19:00">18:00 - 19:00</option>
                          <option value="Evening">Evening (after 19:00)</option>
                        </select>
                      </div>
                    </div>
                  </fieldset>
                  <div>
                    <label htmlFor="additional-notes" className="block text-sm font-medium text-[#444444] mb-2">Additional Notes</label>
                    <textarea id="additional-notes" value={formData.additional_notes} onChange={(e) => updateField("additional_notes", e.target.value)}
                      placeholder="Tell us more about your event, any special requirements..." rows={3} data-testid="additional-notes"
                      className="w-full p-3 md:p-4 rounded-xl border-2 border-gray-200 focus:border-[#a5dae0] focus:ring-2 focus:ring-[#a5dae0]/20 outline-none transition-colors resize-none" />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 6 && (
              <motion.div key="step6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} role="group" aria-labelledby="step6-heading">
                <h3 id="step6-heading" className="text-xl md:text-2xl mb-1 md:mb-2">Drink Preferences & Your Quote</h3>
                <p className="text-[#555555] text-xs md:text-sm mb-4 md:mb-6">Select your preferred drinks from our menu (optional - helps us prepare for your event)</p>
                <div className="space-y-3 md:space-y-4">
                  <DrinkCategorySelect
                    category="draughts"
                    label="Draughts"
                    icon={<Beer size={20} />}
                    items={DRINK_MENU.draughts}
                    selectedItems={formData.drink_preferences.draughts}
                    onToggle={toggleDrinkPreference}
                  />
                  <DrinkCategorySelect
                    category="bottledCanned"
                    label="Bottled & Canned"
                    icon={<Beer size={20} />}
                    items={DRINK_MENU.bottledCanned}
                    selectedItems={formData.drink_preferences.bottledCanned}
                    onToggle={toggleDrinkPreference}
                  />
                  <DrinkCategorySelect
                    category="wines"
                    label="Wines"
                    icon={<Wine size={20} />}
                    items={DRINK_MENU.wines}
                    selectedItems={formData.drink_preferences.wines}
                    onToggle={toggleDrinkPreference}
                  />
                  <DrinkCategorySelect
                    category="spirits"
                    label="Spirits"
                    icon={<GlassWater size={20} />}
                    items={DRINK_MENU.spirits}
                    selectedItems={formData.drink_preferences.spirits}
                    onToggle={toggleDrinkPreference}
                  />
                  <DrinkCategorySelect
                    category="softs"
                    label="Soft Drinks"
                    icon={<GlassWater size={20} />}
                    items={DRINK_MENU.softs}
                    selectedItems={formData.drink_preferences.softs}
                    onToggle={toggleDrinkPreference}
                  />
                  <DrinkCategorySelect
                    category="shots"
                    label="Shots"
                    icon={<GlassWater size={20} />}
                    items={DRINK_MENU.shots}
                    selectedItems={formData.drink_preferences.shots}
                    onToggle={toggleDrinkPreference}
                  />
                </div>

                {priceData && (
                  <div className="bg-[#a5dae0]/10 rounded-xl p-4 md:p-6 mt-6 md:mt-8" role="region" aria-label="Price estimate">
                    <h4 className="font-semibold text-base md:text-lg mb-3 md:mb-4 flex items-center gap-2"><Sparkles size={18} className="text-[#a5dae0]" aria-hidden="true" />Estimated Quote</h4>
                    {renderPriceBreakdown()}
                    <p className="text-xs text-[#555555] mt-3">*Final price may vary based on specific requirements</p>
                  </div>
                )}

                <div className="bg-[#fafafa] rounded-xl p-3 md:p-4 border border-gray-200 mt-6">
                  <div className="flex items-center gap-2 mb-2 md:mb-3">
                    <ShieldCheck size={18} className="text-[#a5dae0]" aria-hidden="true" />
                    <span className="text-sm font-medium text-[#444444]">Security Verification <span className="text-red-500" aria-label="required">*</span></span>
                  </div>
                  <p className="text-xs text-[#555555] mb-3 md:mb-4">Please complete the CAPTCHA to verify you're not a robot.</p>
                  <div className="flex justify-center overflow-x-auto">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={handleRecaptchaExpired}
                      theme="light"
                      size="normal"
                    />
                  </div>
                  {recaptchaToken && (
                    <p className="text-xs text-green-600 text-center mt-2 flex items-center justify-center gap-1" role="status">
                      <Check size={14} aria-hidden="true" /> Verification complete
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between mt-6 md:mt-8 pt-4 md:pt-6 border-t border-gray-100">
            <button 
              onClick={() => setStep(s => Math.max(1, s - 1))} 
              disabled={step === 1} 
              data-testid="form-back"
              aria-label="Go to previous step"
              className={`px-4 md:px-6 py-2.5 md:py-3 rounded-full font-medium transition-all text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${step === 1 ? "opacity-0 pointer-events-none" : "text-[#444444] hover:bg-gray-100"}`}
            >
              Back
            </button>
            {step < getTotalSteps() ? (
              <button 
                onClick={() => setStep(s => s + 1)} 
                disabled={!canProceed()} 
                data-testid="form-next"
                aria-label="Continue to next step"
                className={`px-5 md:px-8 py-2.5 md:py-3 rounded-full font-semibold transition-all flex items-center gap-1 md:gap-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${canProceed() ? "bg-[#a5dae0] text-[#1a1a1a] hover:bg-[#8bc9d0]" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
              >
                Continue <ChevronRight size={16} aria-hidden="true" />
              </button>
            ) : (
              <button 
                onClick={handleSubmit} 
                disabled={!canProceed() || isSubmitting} 
                data-testid="form-submit"
                aria-label={isSubmitting ? "Submitting your inquiry" : "Submit your booking inquiry"}
                className={`px-5 md:px-8 py-2.5 md:py-3 rounded-full font-semibold transition-all flex items-center gap-1 md:gap-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[#a5dae0] focus:ring-offset-2 ${canProceed() && !isSubmitting ? "bg-[#a5dae0] text-[#1a1a1a] hover:bg-[#8bc9d0]" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
              >
                {isSubmitting ? "Submitting..." : "Submit Inquiry"}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BookingForm;
